<template>
  <Modal ref="modalRef" width="32rem" @after-close="reset">
    <template #header>{{ destination ? `Add properties to ${destination.name}` : 'File into a portfolio' }}</template>

    <template #body>
      <View direction="column" gap="md" class="form">
        <FormField v-if="!fixedDestination" label="Portfolio">
          <Select v-model="destinationId">
            <option value="" disabled>Choose a portfolio</option>
            <option v-for="portfolio in portfolios" :key="portfolio.id" :value="portfolio.id">
              {{ labelOf(portfolio) }}
            </option>
          </Select>
        </FormField>

        <Input
          v-if="candidates.length >= searchFrom"
          v-model="search"
          type="search"
          placeholder="Search by address"
          aria-label="Search by address"
        />

        <View direction="column" gap="xs" role="group" aria-label="Properties" class="list">
          <ChecklistRow
            v-for="property in shown"
            :key="property.id"
            :id="`file-property-${property.id}`"
            :title="property.displayName"
            :subtitle="subtitleOf(property)"
            :checked="picked.includes(property.id)"
            @toggle="toggle(property.id, $event)"
          >
            <template #leading>
              <PropertyPhotoPreview :image-url="property.coverImage" size="xs" />
            </template>
          </ChecklistRow>

          <Text v-if="!candidates.length" variant="body-small" color="secondary">{{ nothingToFile }}</Text>
          <Text v-else-if="!shown.length" variant="body-small" color="secondary">No properties match that.</Text>
        </View>

        <Hint icon="info-circle">{{ hint }}</Hint>
      </View>
    </template>

    <template #footer>
      <Button v-if="!fixedDestination" variant="ghost" @click="createInstead">New portfolio instead</Button>
      <Button variant="ghost" @click="close">Cancel</Button>
      <Button :loading="isFiling" :disabled="!destination || !picked.length" @click="file">{{ submitLabel }}</Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { useQueryCache } from '@pinia/colada';
import { storeToRefs } from 'pinia';
import pluralize from 'pluralize';
import { computed, ref } from 'vue';

import {
  Button,
  ChecklistRow,
  FormField,
  Hint,
  Input,
  Modal,
  Select,
  Text,
  View,
  useAlertsStore,
} from '@tallo/design-system';
import { CompanyTeamMemberDto, PortfolioDto, teamMemberName as nameOf, unitsToFile } from '@tallo/investor/company';
import { useInvestorStore } from '@tallo/investor/investor';
import {
  PropertyListItemDto,
  portfolioQueryKey,
  propertySummary,
  useMovePropertyToPortfolio,
} from '@tallo/investor/property';
import { PropertyPhotoPreview } from '@tallo/property';
import { escapeHtml } from '@tallo/utility';

const props = defineProps<{
  members: CompanyTeamMemberDto[];
  portfolios: PortfolioDto[];
  properties: PropertyListItemDto[];
}>();
const emit = defineEmits<{ create: [] }>();

// Below this many properties the whole list fits on screen, and a search box is one more thing to read
const searchFrom = 6;

const alertsStore = useAlertsStore();
const queryCache = useQueryCache();
const modalRef = ref<InstanceType<typeof Modal>>();
const { investor, isAdmin } = storeToRefs(useInvestorStore());
const { mutateAsync } = useMovePropertyToPortfolio();

// A fixed destination is "Add properties to X"; without one the dialog files the loose properties somewhere
const fixedDestination = ref<PortfolioDto>();
const destinationId = ref('');
const picked = ref<string[]>([]);
const search = ref('');
const isFiling = ref(false);

const destination = computed(
  () => fixedDestination.value ?? props.portfolios.find((portfolio) => portfolio.id === destinationId.value),
);
const destinationOwner = computed(() =>
  props.members.find((member) => member.investorId === destination.value?.ownerId),
);

// Only what the viewer runs may move, so the list offers nothing the API would refuse part-way through
// a batch; which portfolio takes it is not limited, since filing a unit you run anywhere is allowed
const candidates = computed(() =>
  unitsToFile(
    { investorId: investor.value?.id, isAdmin: isAdmin.value },
    props.properties,
    props.portfolios,
    fixedDestination.value,
  ),
);

const shown = computed(() => {
  const needle = search.value.trim().toLowerCase();

  return needle
    ? candidates.value.filter((property) => property.displayName.toLowerCase().includes(needle))
    : candidates.value;
});

const nothingToFile = computed(() => {
  if (fixedDestination.value) {
    return 'Every property you can move is already in it.';
  }

  return isAdmin.value ? 'Nothing is unassigned right now.' : 'None of the unassigned properties is yours to file.';
});

const hint = computed(() => {
  const keeps = 'Each property keeps its manager and collaborators.';
  const target = destination.value;

  if (!target) {
    return `${keeps} The portfolio's default collaborators join it, and its owner can reassign or move it from now on.`;
  }

  const runsIt = destinationOwner.value?.isYou
    ? 'you'
    : destinationOwner.value
      ? nameOf(destinationOwner.value)
      : 'its owner';

  return `${keeps} ${target.name}'s default collaborators join it, and ${runsIt} can reassign or move it from now on. Tallo does not message anyone about it.`;
});

const submitLabel = computed(() => {
  const count = picked.value.length;
  const units = count ? pluralize('property', count, true) : 'properties';

  return fixedDestination.value ? `Add ${units}` : `File ${units}`;
});

/** Two portfolios can share a name, so each is told apart by its owner */
function labelOf(portfolio: PortfolioDto): string {
  const owner = props.members.find(({ investorId }) => investorId === portfolio.ownerId);

  return owner ? `${portfolio.name} · ${owner.isYou ? 'you' : nameOf(owner)}` : portfolio.name;
}

function subtitleOf(property: PropertyListItemDto): string {
  return [property.portfolio ? `Moves from ${property.portfolio.name}` : '', propertySummary(property)]
    .filter(Boolean)
    .join(' · ');
}

function toggle(propertyId: string, checked: boolean) {
  picked.value = checked ? [...picked.value, propertyId] : picked.value.filter((id) => id !== propertyId);
}

/** Filing the loose properties starts with all the viewer may move ticked; adding to a portfolio starts blank */
function open(target?: PortfolioDto) {
  reset();
  fixedDestination.value = target;
  picked.value = target ? [] : candidates.value.map((property) => property.id);
  modalRef.value?.show();
}

function close() {
  modalRef.value?.close();
}

function reset() {
  fixedDestination.value = undefined;
  destinationId.value = '';
  picked.value = [];
  search.value = '';
}

function createInstead() {
  close();
  emit('create');
}

/** One move at a time, so a refusal stops the rest and leaves them picked for another try */
async function file() {
  const target = destination.value;

  if (!target) {
    return;
  }

  const targets = candidates.value.filter((property) => picked.value.includes(property.id));
  const moved: string[] = [];

  isFiling.value = true;

  try {
    for (const property of targets) {
      await mutateAsync({ subject: { kind: 'property', id: property.id }, portfolioId: target.id });
      moved.push(property.id);
    }
  } catch {
    // The mutation already showed the server's message; whatever got in is no longer offered
    picked.value = picked.value.filter((id) => !moved.includes(id));

    return;
  } finally {
    isFiling.value = false;
    // Whatever moved changed portfolio, so the page behind the dialog re-reads its list
    if (moved.length) {
      queryCache.invalidateQueries({ key: [portfolioQueryKey] }).catch(() => undefined);
    }
  }

  close();
  alertsStore.success(
    `${pluralize('property', moved.length, true)} ${fixedDestination.value ? 'added to' : 'filed into'} <b>${escapeHtml(target.name)}</b>`,
  );
}

defineExpose({ open });
</script>

<style scoped lang="scss">
.form {
  width: 100%;
}

// The pick list scrolls on its own, so a long list never pushes the hint and the footer out of reach
.list {
  max-height: 18rem;
  overflow-y: auto;
}
</style>
